import { ExplicitIcon, PlayIcon } from "@/assets/icons";
import ShareIcon from "@/assets/icons/share_filled.svg?react";
import {
  useAudioPlayerControls,
  useAudioPlayerQueue,
  useAudioPlayerState,
} from "@/components/providers/AudioPlayerProvider/useAudioPlayer";
import CircularProgress from "@/components/ui/CircularProgress";
import { useTrackPreview } from "@/hooks/useTrackPreview";
import {
  versionDisplayName,
  versionFormat,
  versionMeta,
} from "@/components/audiolab/AlbumVersions/versionKinds";
import type { AlbumTrackExtraAssetDto } from "@/lib/lutely-api/gen/dtos";
import type { AlbumTrackDto } from "@/lib/lutely-api/gen/dtos/albumTrackDto";
import { cn } from "@/lib/utils";
import { Flex, Spinner, Text, Tooltip } from "@radix-ui/themes";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuDownload } from "react-icons/lu";
import { MdOutlineLyrics } from "react-icons/md";
import { RiPauseFill } from "react-icons/ri";

type AlbumTracklistProps = {
  tracks: AlbumTrackDto[];
  /** When true, rows are interactive with 30-second preview playback */
  previewMode?: boolean;
  /** Creator ID for constructing LibraryTrack objects (needed for owned playback) */
  creatorId?: string;
  /** Creator display name (needed for owned playback) */
  creatorName?: string;
  /** Album artwork URL (needed for owned playback) */
  artworkUrl?: string;
  /** Callback when lyrics icon is clicked for a track */
  onLyricsClick?: (trackId: string, clean?: boolean) => void;
  /** Lyrics stay locked until the release is purchased. */
  lyricsLocked?: boolean;
  /** When true, all audio interaction is disabled (pre-release albums) */
  noPlayback?: boolean;
  /** Shares one track */
  onShareTrack?: (track: AlbumTrackDto) => void;
  /** Draft album: keep the share buttons visible but inert */
  shareDisabled?: boolean;
  /** Super Singles list each version as its own numbered track. */
  versionsAsTracks?: boolean;
};

type TracklistRow = {
  track: AlbumTrackDto;
  /** The album track a version row belongs to (its own id for tracks). */
  sourceTrackId: string;
  isVersion: boolean;
  versionLabel?: string;
};

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return "-";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatCountdown(elapsedMs: number, maxMs: number): string {
  const remainingMs = Math.max(0, maxMs - elapsedMs);
  const totalSecs = Math.ceil(remainingMs / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function RowShareButton({
  title,
  disabled,
  onShare,
}: {
  title: string | null;
  disabled?: boolean;
  onShare: () => void;
}) {
  const button = (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onShare();
      }}
      className="w-[34px] h-[34px] flex items-center justify-center rounded-full hover:bg-white/20 text-white transition-all cursor-pointer pointer-events-auto disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      aria-label={`Share ${title || "track"}`}
      data-testid="tracklist-share-button"
    >
      <ShareIcon width={16} height={16} />
    </button>
  );
  return (
    <Tooltip content={disabled ? "Publish first to share" : "Share"}>
      {/* Disabled buttons swallow pointer events; the span keeps the tooltip alive */}
      {disabled ? <span className="inline-flex">{button}</span> : button}
    </Tooltip>
  );
}

function RowLyricsButton({
  title,
  lockedReason,
  onOpen,
}: {
  title: string | null;
  lockedReason?: string;
  onOpen: () => void;
}) {
  const button = (
    <button
      type="button"
      disabled={!!lockedReason}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
      className="lyrics-button w-[34px] h-[34px] flex items-center justify-center gap-1 aspect-square rounded-full hover:bg-white/20 text-white hover:text-[#43DDA7] transition-all cursor-pointer pointer-events-auto disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-white"
      aria-label={lockedReason ?? `View lyrics for ${title || "track"}`}
      data-testid="tracklist-lyrics-button"
    >
      <MdOutlineLyrics className="w-4 h-4 transition-transform [.lyrics-button:enabled:hover_&]:scale-[1.2]" />
    </button>
  );
  return (
    <Tooltip content={lockedReason ?? "Lyrics"}>
      {lockedReason ? (
        // Disabled buttons swallow pointer events; the span keeps the tooltip and stops the row from playing.
        <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
          {button}
        </span>
      ) : (
        button
      )}
    </Tooltip>
  );
}

const LYRICS_AFTER_PURCHASE = "Lyrics available after purchase";

// --- Sub-component: a single track row with preview interaction ---

const TrackPreviewRow = memo(function TrackPreviewRow({
  track,
  isActive,
  isPreviewPlaying,
  isPreviewLoading,
  previewTimeMs,
  maxDurationMs,
  onTogglePlay,
  onLyricsClick,
  lyricsLockedReason,
  onShare,
  shareDisabled,
}: {
  track: AlbumTrackDto;
  isActive: boolean;
  isPreviewPlaying: boolean;
  isPreviewLoading: boolean;
  previewTimeMs: number;
  maxDurationMs: number;
  onTogglePlay: () => void;
  onLyricsClick?: (trackId: string) => void;
  lyricsLockedReason?: string;
  onShare?: () => void;
  shareDisabled?: boolean;
}) {
  const previewProgress = maxDurationMs > 0 ? previewTimeMs / maxDurationMs : 0;
  const [isHovered, setIsHovered] = useState(false);
  const hasAudio = !!track.fileUrl;
  const showPlayButton = hasAudio && (isHovered || isActive);
  /** Show the ring + pause icon immediately on click (loading or playing) */
  const showRing = isActive && (isPreviewPlaying || isPreviewLoading);

  return (
    <Flex
      className={cn(
        "h-14 items-center gap-4 px-2 group rounded-sm select-none",
        hasAudio ? "cursor-pointer" : "cursor-default",
        isActive
          ? "bg-[rgba(255,255,255,0.15)]"
          : "hover:bg-[rgba(255,255,255,0.05)]",
      )}
      justify="between"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={hasAudio ? () => onTogglePlay() : undefined}
    >
      <Flex className="items-center gap-4 flex-1 min-w-0">
        {/* Position number / Play button container */}
        <div className="relative w-[34px] h-[34px] flex items-center justify-center shrink-0">
          {/* Position number */}
          <Text
            className={cn(
              "text-[14px] font-normal leading-[140%] text-white text-right tabular-nums absolute transition-opacity duration-150",
              showPlayButton ? "opacity-0" : "opacity-100",
            )}
          >
            {track.position + 1}
          </Text>

          {/* Play/Pause button */}
          {hasAudio && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTogglePlay();
              }}
              className={cn(
                "absolute inset-0 flex items-center justify-center transition-opacity duration-150 cursor-pointer border border-white/10 bg-white/15 backdrop-blur-[5px] rounded-full p-[5.5px]",
                showPlayButton
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none",
              )}
              aria-label={
                showRing
                  ? `Pause ${track.title || "track"}`
                  : `Preview ${track.title || "track"}`
              }
            >
              {/* Play icon — visible when not actively playing/loading */}
              <PlayIcon
                className={cn(
                  "w-6 h-6 text-white absolute transition-opacity duration-150",
                  showRing ? "opacity-0" : "opacity-100",
                )}
              />
              {/* Circular progress ring — mounted when active, visible when playing or loading */}
              {isActive && (
                <div
                  className={cn(
                    "absolute transition-opacity duration-150",
                    showRing ? "opacity-100" : "opacity-0",
                  )}
                >
                  <CircularProgress
                    progress={previewProgress}
                    size={34}
                    strokeWidth={2}
                    color="#3042E2"
                    trackColor="rgba(255,255,255,0.10)"
                    loading={isPreviewLoading}
                  >
                    <RiPauseFill className="text-[16px] text-white" />
                  </CircularProgress>
                </div>
              )}
            </button>
          )}
        </div>

        <Flex className="justify-center items-center gap-1 min-w-0">
          <Text className="text-[14px] font-semibold leading-[140%] text-white truncate">
            {track.title || "Untitled"}
          </Text>
          {track.isExplicit && (
            <Tooltip content="Explicit">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <ExplicitIcon
                  className="w-4 h-4"
                  style={{ fill: "rgba(255, 255, 255, 0.40)" }}
                />
              </div>
            </Tooltip>
          )}
          {!!track.promotionalTrack && (
            <Tooltip content="Promotional Single">
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide leading-none text-[#43DDA7] border border-[#43DDA7]/50 rounded px-1 py-0.5">
                Promo
              </span>
            </Tooltip>
          )}
        </Flex>
      </Flex>
      {/* No track-level heart (LUT-883): tracks inside an album cannot be
          bought individually, so a like per row only added clutter and
          competed with the album's buy CTA. Liking lives at the album level
          here; standalone single pages keep their own heart. */}
      <Flex className="items-center gap-2 shrink-0">
        {onShare && (
          <RowShareButton
            title={track.title}
            disabled={shareDisabled}
            onShare={onShare}
          />
        )}
        {onLyricsClick && (
          <RowLyricsButton
            title={track.title}
            lockedReason={lyricsLockedReason}
            onOpen={() => onLyricsClick(track.trackId)}
          />
        )}
        <Text className="text-[14px] font-normal leading-[140%] text-white/80 text-right tabular-nums">
          {isActive && (isPreviewPlaying || isPreviewLoading)
            ? formatCountdown(previewTimeMs, maxDurationMs)
            : formatDuration(track.duration)}
        </Text>
      </Flex>
    </Flex>
  );
});

// --- Full playback row (owned album — wired to global audio player) ---

const TrackFullRow = memo(function TrackFullRow({
  track,
  isActiveInGlobal,
  isGlobalPlaying,
  isLoading,
  progress,
  onTogglePlay,
  onLyricsClick,
  lyricsLockedReason,
  onShare,
  shareDisabled,
}: {
  track: AlbumTrackDto;
  isActiveInGlobal: boolean;
  isGlobalPlaying: boolean;
  isLoading: boolean;
  progress: number;
  onTogglePlay: () => void;
  onLyricsClick?: (trackId: string) => void;
  lyricsLockedReason?: string;
  onShare?: () => void;
  shareDisabled?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const hasAudio = !!track.fileUrl;
  const isThisPlaying = isActiveInGlobal && isGlobalPlaying;
  const showPlayButton = hasAudio && (isHovered || isActiveInGlobal);
  /** Show the ring + pause icon immediately on click (loading or playing) */
  const showRing = isActiveInGlobal && (isThisPlaying || isLoading);

  return (
    <Flex
      className={cn(
        "h-14 items-center gap-4 px-2 group rounded-sm select-none",
        hasAudio ? "cursor-pointer" : "cursor-default",
        isActiveInGlobal
          ? "bg-[rgba(255,255,255,0.15)]"
          : "hover:bg-[rgba(255,255,255,0.05)]",
      )}
      justify="between"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={hasAudio ? () => onTogglePlay() : undefined}
    >
      <Flex className="items-center gap-4 flex-1 min-w-0">
        {/* Position number / Play button container */}
        <div className="relative w-[34px] h-[34px] flex items-center justify-center shrink-0">
          {/* Position number — hidden when play button shows */}
          <Text
            className={cn(
              "text-[14px] font-normal leading-[140%] text-white text-right tabular-nums absolute transition-opacity duration-150",
              showPlayButton ? "opacity-0" : "opacity-100",
            )}
          >
            {track.position + 1}
          </Text>

          {/* Play/Pause button */}
          {hasAudio && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTogglePlay();
              }}
              className={cn(
                "absolute inset-0 flex items-center justify-center transition-opacity duration-150 cursor-pointer border border-white/10 bg-white/15 backdrop-blur-[5px] rounded-full p-[5.5px]",
                showPlayButton
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none",
              )}
              aria-label={
                showRing
                  ? `Pause ${track.title || "track"}`
                  : `Play ${track.title || "track"}`
              }
            >
              {/* Loading spinner */}
              {isLoading && <Spinner size="1" className="text-white" />}
              {/* Play icon — visible when not actively playing/loading */}
              {!isLoading && (
                <PlayIcon
                  className={cn(
                    "w-6 h-6 text-white absolute transition-opacity duration-150",
                    showRing ? "opacity-0" : "opacity-100",
                  )}
                />
              )}
              {/* Circular progress ring — mounted when active, visible when playing (not loading) */}
              {isActiveInGlobal && !isLoading && (
                <div
                  className={cn(
                    "absolute transition-opacity duration-150",
                    showRing ? "opacity-100" : "opacity-0",
                  )}
                >
                  <CircularProgress
                    progress={progress}
                    size={34}
                    strokeWidth={2}
                    color="#3042E2"
                    trackColor="rgba(255,255,255,0.10)"
                    loading={false}
                  >
                    <RiPauseFill className="text-[16px] text-white" />
                  </CircularProgress>
                </div>
              )}
            </button>
          )}
        </div>

        <Flex className="justify-center items-center gap-1 min-w-0">
          <Text className="text-[14px] font-semibold leading-[140%] text-white truncate">
            {track.title || "Untitled"}
          </Text>
          {track.isExplicit && (
            <Tooltip content="Explicit">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <ExplicitIcon
                  className="w-4 h-4"
                  style={{ fill: "rgba(255, 255, 255, 0.40)" }}
                />
              </div>
            </Tooltip>
          )}
        </Flex>
      </Flex>
      <Flex className="items-center gap-2 shrink-0">
        {onShare && (
          <RowShareButton
            title={track.title}
            disabled={shareDisabled}
            onShare={onShare}
          />
        )}
        {onLyricsClick && (
          <RowLyricsButton
            title={track.title}
            lockedReason={lyricsLockedReason}
            onOpen={() => onLyricsClick(track.trackId)}
          />
        )}
        <Text className="text-[14px] font-normal leading-[140%] text-white/80 text-right tabular-nums">
          {formatDuration(track.duration)}
        </Text>
      </Flex>
    </Flex>
  );
});

// --- Static row (pre-release — no audio interaction) ---

const TrackStaticRow = memo(function TrackStaticRow({
  track,
  onLyricsClick,
  onShare,
  shareDisabled,
}: {
  track: AlbumTrackDto;
  onLyricsClick?: (trackId: string) => void;
  onShare?: () => void;
  shareDisabled?: boolean;
}) {
  return (
    <Flex
      className="h-14 items-center gap-4 px-2 rounded-sm cursor-default select-none"
      justify="between"
    >
      <Flex className="items-center gap-4 flex-1 min-w-0">
        <div className="relative w-[34px] h-[34px] flex items-center justify-center shrink-0">
          <Text className="text-[14px] font-normal leading-[140%] text-white/60 text-right tabular-nums">
            {track.position + 1}
          </Text>
        </div>
        <Flex className="justify-center items-center gap-1 min-w-0">
          <Text className="text-[14px] font-semibold leading-[140%] text-white/60 truncate">
            {track.title || "Untitled"}
          </Text>
          {track.isExplicit && (
            <Tooltip content="Explicit">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <ExplicitIcon
                  className="w-4 h-4"
                  style={{ fill: "rgba(255, 255, 255, 0.25)" }}
                />
              </div>
            </Tooltip>
          )}
        </Flex>
      </Flex>
      <Flex className="items-center gap-2 shrink-0">
        {onShare && (
          <RowShareButton
            title={track.title}
            disabled={shareDisabled}
            onShare={onShare}
          />
        )}
        {onLyricsClick && (
          <RowLyricsButton
            title={track.title}
            lockedReason="Lyrics available after release"
            onOpen={() => onLyricsClick(track.trackId)}
          />
        )}
        <Text className="text-[14px] font-normal leading-[140%] text-white/40 text-right tabular-nums">
          {formatDuration(track.duration)}
        </Text>
      </Flex>
    </Flex>
  );
});

// --- Included versions (LUT-901), listed under their track ---

// Buyers and the owner get download links; everyone else sees the names.
function TrackVersionRows({
  versions,
  muted,
  downloadable,
}: {
  versions?: AlbumTrackExtraAssetDto[];
  muted?: boolean;
  /** Only buyers download; the owner's own view stays a preview. */
  downloadable?: boolean;
}) {
  if (!versions?.length) return null;

  return (
    <Flex direction="column" className="pb-2" data-testid="tracklist-versions">
      {versions.map((version) => {
        const meta = versionMeta(version.label);
        const name = meta.publicName ?? meta.name;
        const format = versionFormat(version.mimeType);
        return (
          <Flex
            key={version.trackAssetId}
            className="h-9 items-center gap-3 pl-[58px] pr-2"
            data-testid={`tracklist-version-${version.label.toLowerCase()}`}
          >
            <meta.Icon
              className={cn(
                "h-[14px] w-[14px] flex-none fill-current",
                muted ? "text-white/40" : "text-white/70",
              )}
            />
            <Text
              className={cn(
                "min-w-0 flex-1 truncate text-[13px] font-medium leading-[140%]",
                muted ? "text-white/40" : "text-white/80",
              )}
            >
              {name}
              {format && (
                <span className="ml-2 text-[11px] text-white/40">{format}</span>
              )}
            </Text>
            {downloadable && version.fileUrl && !muted && (
              <Tooltip content="Download">
                <a
                  href={version.fileUrl}
                  className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full text-white transition-all hover:bg-white/20 hover:text-[#43DDA7]"
                  aria-label={`Download ${name}`}
                  data-testid="tracklist-version-download"
                >
                  <LuDownload className="h-4 w-4" />
                </a>
              </Tooltip>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

// --- Main component ---

export default function AlbumTracklist({
  tracks,
  previewMode,
  creatorId,
  creatorName,
  artworkUrl,
  onLyricsClick,
  lyricsLocked,
  noPlayback,
  onShareTrack,
  shareDisabled,
  versionsAsTracks,
}: AlbumTracklistProps) {
  const { pause: pauseGlobalPlayer, togglePlay } = useAudioPlayerControls();
  const {
    isPlaying: globalIsPlaying,
    currentTrackId: globalCurrentTrackId,
    isReady: globalIsReady,
    progress: globalProgress,
  } = useAudioPlayerState();
  const { registerTracks, setQueue } = useAudioPlayerQueue();

  const sortedTracks = useMemo(
    () => [...tracks].sort((a, b) => a.position - b.position),
    [tracks],
  );

  // A version plays and reads like a track: "Title (Clean)", numbered after
  // its track. Only buyers (and the owner) get its audio.
  const rows = useMemo<TracklistRow[]>(() => {
    if (!versionsAsTracks)
      return sortedTracks.map((track) => ({
        track,
        sourceTrackId: track.trackId,
        isVersion: false,
      }));
    const out: TracklistRow[] = [];
    for (const track of sortedTracks) {
      out.push({
        track: { ...track, position: out.length },
        sourceTrackId: track.trackId,
        isVersion: false,
      });
      for (const version of track.extraAssets ?? []) {
        out.push({
          track: {
            ...track,
            trackId: `${track.trackId}:${version.trackAssetId}`,
            title: `${track.title || "Untitled"} (${versionDisplayName(version.label)})`,
            fileUrl: version.fileUrl ?? null,
            position: out.length,
            promotionalTrack: false,
            isExplicit: version.label === "Vocals" && track.isExplicit,
            extraAssets: [],
          },
          sourceTrackId: track.trackId,
          isVersion: true,
          versionLabel: version.label,
        });
      }
    }
    return out;
  }, [sortedTracks, versionsAsTracks]);

  // --- Preview mode state (only active when previewMode=true) ---

  const autoAdvanceTimerRef = useRef(0);
  const playRef = useRef<(src: string, trackId: string) => void>(() => {});
  const promoPlayRef = useRef<(src: string, trackId: string) => void>(() => {});

  const handleTrackEnd = useCallback(
    (endedTrackId: string) => {
      const currentIndex = rows.findIndex(
        (r) => r.track.trackId === endedTrackId,
      );
      const nextTrack = rows[currentIndex + 1]?.track;
      if (nextTrack?.fileUrl) {
        const isNextPromo = nextTrack.promotionalTrack;
        autoAdvanceTimerRef.current = window.setTimeout(() => {
          if (isNextPromo) {
            promoPlayRef.current(nextTrack.fileUrl!, nextTrack.trackId);
          } else {
            playRef.current(nextTrack.fileUrl!, nextTrack.trackId);
          }
        }, 800);
      }
    },
    [rows],
  );

  const preview = useTrackPreview({
    maxDurationMs: 30_000,
    onTrackEnd: handleTrackEnd,
    pauseGlobalPlayer,
  });

  const promoPreview = useTrackPreview({
    maxDurationMs: 600_000,
    onTrackEnd: handleTrackEnd,
    pauseGlobalPlayer,
  });

  playRef.current = preview.play;
  promoPlayRef.current = promoPreview.play;

  // Pause previews if global player starts
  useEffect(() => {
    if (globalIsPlaying) {
      if (preview.isPlaying) preview.pause();
      if (promoPreview.isPlaying) promoPreview.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalIsPlaying]);

  // Cleanup auto-advance timer
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

  // --- Owned mode: handle play for full tracks via global player ---

  const handleFullTrackPlay = useCallback(
    (trackId: string) => {
      // If this track is already active in the global player, just toggle
      if (globalCurrentTrackId === trackId) {
        togglePlay();
        return;
      }

      // Register all album tracks with the global player
      const libraryTracks = rows
        .filter(({ track: t }) => t.fileUrl)
        .map(({ track: t, sourceTrackId }) => ({
          id: t.trackId,
          trackId: sourceTrackId,
          title: t.title || "Untitled",
          creatorId: creatorId || "",
          creator: creatorName || "",
          artworkUrl: artworkUrl || "",
          src: t.fileUrl!,
          durationMs: t.duration ? t.duration * 1000 : undefined,
        }));

      registerTracks(libraryTracks);

      // Set the full album as a queue starting from the clicked track
      setQueue({
        trackIds: libraryTracks.map((t) => t.id),
        startTrackId: trackId,
        autoPlay: true,
        startPositionMs: 0,
        replace: true,
      });
    },
    [
      globalCurrentTrackId,
      rows,
      creatorId,
      creatorName,
      artworkUrl,
      registerTracks,
      setQueue,
      togglePlay,
    ],
  );

  if (!tracks || tracks.length === 0) {
    return (
      <Text className="text-text-secondary text-base">
        No tracks in this album
      </Text>
    );
  }

  return (
    <Flex direction="column" className="w-full">
      {rows.map(({ track, sourceTrackId, isVersion, versionLabel }) => (
        <Flex key={track.trackId} direction="column">
          <div
            className="w-full h-[1px]"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.15)" }}
          />

          {(() => {
            const isPromo = track.promotionalTrack;
            const activePreview = isPromo ? promoPreview : preview;
            const trackMaxMs = isPromo
              ? (track.duration ?? 300) * 1000
              : 30_000;

            // Sharing belongs to the track; the Clean version gets clean lyrics.
            const onShare =
              onShareTrack && !isVersion
                ? () => onShareTrack(track)
                : undefined;
            const onLyrics =
              !onLyricsClick || !isVersion
                ? onLyricsClick
                : versionLabel === "Clean"
                  ? () => onLyricsClick(sourceTrackId, true)
                  : undefined;
            const lyricsLockedReason = lyricsLocked
              ? LYRICS_AFTER_PURCHASE
              : undefined;

            if (noPlayback) {
              return (
                <TrackStaticRow
                  track={track}
                  onLyricsClick={onLyrics}
                  onShare={onShare}
                  shareDisabled={shareDisabled}
                />
              );
            }

            if (previewMode) {
              return (
                <TrackPreviewRow
                  track={track}
                  isActive={activePreview.currentTrackId === track.trackId}
                  isPreviewPlaying={
                    activePreview.currentTrackId === track.trackId &&
                    activePreview.isPlaying
                  }
                  isPreviewLoading={
                    activePreview.currentTrackId === track.trackId &&
                    activePreview.isLoading
                  }
                  previewTimeMs={
                    activePreview.currentTrackId === track.trackId
                      ? activePreview.currentTimeMs
                      : 0
                  }
                  maxDurationMs={trackMaxMs}
                  onTogglePlay={() => {
                    if (autoAdvanceTimerRef.current) {
                      clearTimeout(autoAdvanceTimerRef.current);
                      autoAdvanceTimerRef.current = 0;
                    }
                    if (track.fileUrl) {
                      if (isPromo || track.promotionalTrack) {
                        preview.stop();
                        promoPreview.togglePlay(track.fileUrl, track.trackId);
                      } else {
                        promoPreview.stop();
                        preview.togglePlay(track.fileUrl, track.trackId);
                      }
                    }
                  }}
                  onLyricsClick={onLyrics}
                  lyricsLockedReason={lyricsLockedReason}
                  onShare={onShare}
                  shareDisabled={shareDisabled}
                />
              );
            }

            return (
              <TrackFullRow
                track={track}
                isActiveInGlobal={globalCurrentTrackId === track.trackId}
                isGlobalPlaying={globalIsPlaying}
                isLoading={
                  globalCurrentTrackId === track.trackId && !globalIsReady
                }
                progress={
                  globalCurrentTrackId === track.trackId ? globalProgress : 0
                }
                onTogglePlay={() => handleFullTrackPlay(track.trackId)}
                onLyricsClick={onLyrics}
                lyricsLockedReason={lyricsLockedReason}
                onShare={onShare}
                shareDisabled={shareDisabled}
              />
            );
          })()}
          {!versionsAsTracks && (
            <TrackVersionRows
              versions={track.extraAssets}
              muted={noPlayback}
              downloadable={!previewMode}
            />
          )}
        </Flex>
      ))}
    </Flex>
  );
}
